import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { recurringBillingEndpoints } from '@/lib/api/endpoints';
import { recurringBillingKeys } from '@/lib/queryKeys';
import { repairLinkOutcome } from '@/lib/recurring-billing/viewModel';
import type {
  PaginatedRecurringBillingPlans,
  PaginatedRecurringBillingRuns,
  RecurringBillingRepairLinkResponse,
  RecurringBillingPlanSummary,
  RecurringBillingRunSummary,
  RecurringPeriodInvoiceResult,
  RecurringSeriesChargeResult,
} from '@/types/recurring-billing';
import type { RecurringStatus } from '@/types/job';

export interface RecurringBillingPlanListParams {
  page?: number;
  limit?: number;
  status?: string;
  recurringStatus?: RecurringStatus;
  jobId?: string;
  contactId?: string;
  autoCharge?: boolean;
}

export interface RecurringBillingRunListParams {
  page?: number;
  limit?: number;
  status?: string;
  planId?: string;
  jobId?: string;
  contactId?: string;
}

export interface RecurringBillingListOptions {
  enabled?: boolean;
}

function invalidateRecurringBilling(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: recurringBillingKeys.all() });
}

export function useRecurringBillingPlans(
  params: RecurringBillingPlanListParams = {},
  options: RecurringBillingListOptions = {},
) {
  return useApiQuery<PaginatedRecurringBillingPlans>({
    queryKey: recurringBillingKeys.planList(params),
    queryFn: () =>
      apiGet<PaginatedRecurringBillingPlans>(operationsClient, recurringBillingEndpoints.plans(), {
        params,
      }),
    enabled: options.enabled ?? true,
    showErrorToast: false,
  });
}

export function useRecurringBillingPlan(id: string) {
  return useApiQuery<RecurringBillingPlanSummary>({
    queryKey: recurringBillingKeys.planDetail(id),
    queryFn: () =>
      apiGet<RecurringBillingPlanSummary>(operationsClient, recurringBillingEndpoints.plan(id)),
    enabled: Boolean(id),
    showErrorToast: false,
  });
}

/** How often the runs list asks again while a run is on its way. */
export const RUN_FOLLOW_POLL_MS = 2_000;
/** How long after a dispatch the deferred runs it queued are waited on. */
export const RUN_FOLLOW_WINDOW_MS = 30_000;

/**
 * Whether the runs list still shows a run that is about to change. A dispatch
 * only queues the due runs and a worker bills them a moment later, so the one
 * refetch as the dispatch answered left them "Deferred" or "Processing" until
 * a reload. A deferred run is waited on only right after a dispatch: one whose
 * plan is paused stays deferred.
 */
export function shouldFollowRuns(
  page: PaginatedRecurringBillingRuns | undefined,
  dispatchedAt: number | null,
  now: number = Date.now(),
): boolean {
  const runs = page?.data ?? [];
  if (runs.some((run) => run.status === 'PROCESSING')) return true;
  return (
    dispatchedAt !== null &&
    now - dispatchedAt < RUN_FOLLOW_WINDOW_MS &&
    runs.some((run) => run.status === 'DEFERRED')
  );
}

export function useRecurringBillingRuns(
  params: RecurringBillingRunListParams = {},
  options: { dispatchedAt?: number | null } = {},
) {
  const queryClient = useQueryClient();
  const dispatchedAt = options.dispatchedAt ?? null;
  const query = useApiQuery<PaginatedRecurringBillingRuns>({
    queryKey: recurringBillingKeys.runList(params),
    queryFn: () =>
      apiGet<PaginatedRecurringBillingRuns>(operationsClient, recurringBillingEndpoints.runs(), {
        params,
      }),
    refetchInterval: (current) =>
      shouldFollowRuns(current.state.data, dispatchedAt) ? RUN_FOLLOW_POLL_MS : false,
    showErrorToast: false,
  });

  // Once the runs settle, the plans and counts beside them catch up once.
  const following = shouldFollowRuns(query.data, dispatchedAt);
  const wasFollowing = useRef(false);
  useEffect(() => {
    if (wasFollowing.current && !following) invalidateRecurringBilling(queryClient);
    wasFollowing.current = following;
  }, [following, queryClient]);

  return query;
}

export function useRecurringBillingExceptions(
  params: {
    page?: number;
    limit?: number;
    jobId?: string;
    contactId?: string;
  } = {},
) {
  return useApiQuery<PaginatedRecurringBillingPlans>({
    queryKey: recurringBillingKeys.exceptions(params),
    queryFn: () =>
      apiGet<PaginatedRecurringBillingPlans>(
        operationsClient,
        recurringBillingEndpoints.exceptions(),
        { params },
      ),
    showErrorToast: false,
  });
}

export function usePauseRecurringBillingPlan() {
  const queryClient = useQueryClient();
  return useApiMutation<RecurringBillingPlanSummary, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<RecurringBillingPlanSummary, Record<string, never>>(
        operationsClient,
        recurringBillingEndpoints.pausePlan(id),
        {},
      ),
    onSuccess: () => {
      invalidateRecurringBilling(queryClient);
      toast.success('Recurring billing plan paused');
    },
  });
}

export function useResumeRecurringBillingPlan() {
  const queryClient = useQueryClient();
  return useApiMutation<RecurringBillingPlanSummary, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<RecurringBillingPlanSummary, Record<string, never>>(
        operationsClient,
        recurringBillingEndpoints.resumePlan(id),
        {},
      ),
    onSuccess: () => {
      invalidateRecurringBilling(queryClient);
      toast.success('Recurring billing plan resumed');
    },
  });
}

type RunOrChargeResult = RecurringBillingRunSummary | RecurringSeriesChargeResult | null;

/** Retry can also bill a subscription period the billing pass could not invoice. */
type RetryResult = RunOrChargeResult | RecurringPeriodInvoiceResult;

/**
 * A run or retry that did nothing answers with an empty body, which arrives as
 * an empty string rather than null: only an object carries a result kind.
 */
function resultKind(result: RetryResult): string | null {
  return typeof result === 'object' && result !== null && 'kind' in result ? result.kind : null;
}

function isSeriesCharge(result: RetryResult): result is RecurringSeriesChargeResult {
  return resultKind(result) === 'CHARGE';
}

function isPeriodInvoice(result: RetryResult): result is RecurringPeriodInvoiceResult {
  return resultKind(result) === 'INVOICE';
}

/** Say what charging a subscription or prepaid series' invoices did. */
function toastSeriesCharge(result: RecurringSeriesChargeResult) {
  if (result.failed > 0) {
    toast.error(`Charge failed: ${result.reason ?? 'the payment did not go through'}`);
    return;
  }
  if (result.charged > 0) {
    const amount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(result.amountCharged);
    toast.success(
      `Charged ${amount} on ${result.charged} invoice${result.charged === 1 ? '' : 's'}`,
    );
    return;
  }
  toast.success('Nothing is owed on this series');
}

export function useRunRecurringBillingPlanNow() {
  const queryClient = useQueryClient();
  return useApiMutation<RunOrChargeResult, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<RunOrChargeResult, Record<string, never>>(
        operationsClient,
        recurringBillingEndpoints.runPlanNow(id),
        {},
      ),
    onSuccess: (result) => {
      invalidateRecurringBilling(queryClient);
      // A subscription or prepaid series has no runs: it charged what it owes.
      if (isSeriesCharge(result)) {
        toastSeriesCharge(result);
        return;
      }
      // A per-completion series with every completed visit billed, or a batch
      // with nothing owed, runs nothing.
      toast.success(
        result && result.status !== 'SKIPPED'
          ? 'Recurring billing run started'
          : 'Nothing to bill right now',
      );
    },
  });
}

export function useRetryRecurringBillingPlanFailure() {
  const queryClient = useQueryClient();
  return useApiMutation<RetryResult, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<RetryResult, Record<string, never>>(
        operationsClient,
        recurringBillingEndpoints.retryPlanFailure(id),
        {},
      ),
    onSuccess: (result) => {
      invalidateRecurringBilling(queryClient);
      if (isSeriesCharge(result)) {
        toastSeriesCharge(result);
        return;
      }
      if (isPeriodInvoice(result)) {
        toast.success(result.invoiceId ? 'Period invoiced' : 'Nothing to bill right now');
        return;
      }
      toast.success('Retry started for recurring billing failure');
    },
  });
}

export function useUpdateRecurringPlanPaymentMethod() {
  const queryClient = useQueryClient();
  return useApiMutation<RecurringBillingPlanSummary, { id: string; paymentMethodId: string }>({
    mutationFn: ({ id, paymentMethodId }) =>
      apiPatch<RecurringBillingPlanSummary, { paymentMethodId: string }>(
        operationsClient,
        recurringBillingEndpoints.updatePlanPaymentMethod(id),
        { paymentMethodId },
      ),
    onSuccess: () => {
      invalidateRecurringBilling(queryClient);
      toast.success('Recurring charge method updated');
    },
  });
}

export function useDispatchRecurringBillingNow() {
  const queryClient = useQueryClient();
  return useApiMutation<{ dispatched: number }, void>({
    mutationFn: () =>
      apiPost<{ dispatched: number }, Record<string, never>>(
        operationsClient,
        recurringBillingEndpoints.dispatchNow(),
        {},
      ),
    onSuccess: (data) => {
      invalidateRecurringBilling(queryClient);
      toast.success(
        data.dispatched === 1
          ? 'Dispatched 1 due recurring plan'
          : `Dispatched ${data.dispatched} due recurring plans`,
      );
    },
  });
}

export function useCreateRecurringBillingRepairLink() {
  const queryClient = useQueryClient();
  return useApiMutation<RecurringBillingRepairLinkResponse, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost<RecurringBillingRepairLinkResponse, Record<string, never>>(
        operationsClient,
        recurringBillingEndpoints.createRepairLink(id),
        {},
      ),
    onSuccess: (data) => {
      invalidateRecurringBilling(queryClient);
      const outcome = repairLinkOutcome(data);
      if (outcome.emailed) toast.success(outcome.message);
      else toast.warning(outcome.message);
    },
  });
}
